import { Box, CircularProgress, Typography } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useUserApi } from '../../../services/api/useUser.api';
import { ApiResponse } from '../../../types/ApiResponse.type';
import { Case } from '../../../types/Case/Case.type';
import { GridView } from './Grid/GridView';
import { ListView } from './List/ListView';
import Toolbar from './Toolbar';

const SavedSearches = () => {
  const VIEW_OPTION_STORAGE_KEY = 'saved-searches-view-option';

  const { getUserCases, getUserInfo } = useUserApi();
  const [sortBy, setSortBy] = useState('updated_at:desc');
  const [viewOption, setViewOption] = useState<'list' | 'grid'>(() => {
    const storedViewOption = localStorage.getItem(VIEW_OPTION_STORAGE_KEY);

    if (storedViewOption === 'list' || storedViewOption === 'grid') {
      return storedViewOption;
    }

    return 'list';
  });
  const [query, setQuery] = useState('');

  useEffect(() => {
    localStorage.setItem(VIEW_OPTION_STORAGE_KEY, viewOption);
  }, [viewOption]);

  const { data: userInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: getUserInfo,
  });
  const user_id = userInfo?.id;

  const {
    data: infiniteCases,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['userCases', user_id, sortBy, query],
    queryFn: async ({ pageParam = 1 }) => {
      if (!user_id) {
        throw new Error();
      }
      return await getUserCases(user_id, { page: pageParam, per_page: 20, sort: sortBy, query });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Case[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: !!user_id,
  });

  const handleSort = (newSortBy: string) => {
    setSortBy(newSortBy);
  };

  const handleSearch = (newSearchQuery: string) => {
    setQuery(newSearchQuery);
    refetch();
  };

  const toggleView = () => {
    setViewOption(viewOption === 'grid' ? 'list' : 'grid');
  };

  const cases = infiniteCases?.pages?.flatMap((page) => page.data) ?? [];

  return (
    <>
      <Toolbar
        onSort={handleSort}
        activeSort={sortBy}
        viewOption={viewOption}
        toggleView={toggleView}
        onSearch={handleSearch}
      />
      {isLoading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '620px' }}>
          <CircularProgress />
        </Box>
      ) : cases.length === 0 ? (
        <Box
          sx={{
            padding: 6,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant='body2'>No cases found</Typography>
        </Box>
      ) : (
        <>
          {viewOption === 'grid' ? (
            <GridView
              activeSort={sortBy}
              cases={cases}
              fetchNextPage={hasNextPage ? fetchNextPage : () => {}}
              loadingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
            />
          ) : (
            <ListView
              activeSort={sortBy}
              cases={cases}
              fetchNextPage={hasNextPage ? fetchNextPage : () => {}}
              loadingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
            />
          )}
        </>
      )}
    </>
  );
};

export default SavedSearches;
